import { format as formatDate, getDaysInMonth } from 'date-fns';
import range from 'lodash/range';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@anytag/shared-utils';
import { Select } from '@anytag/shared-atoms';
import { SelectOption } from '@anytag/shared-types';

export interface YearMonthDayPickerProps {
  format: string;
  maxDate?: Date;
  minDate?: Date;
  disabled?: boolean;
  className?: string;
  hasError?: boolean;
  value?: Date | string;
  onChange?: (date: string) => void;
}

export const YearMonthDayPicker = ({
  disabled,
  format,
  maxDate,
  minDate,
  value,
  onChange,
  className,
  hasError
}: YearMonthDayPickerProps) => {
  const { t } = useTranslation();
  const [selectedDay, setSelectedDay] = useState<string>('');
  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const [selectedYear, setSelectedYear] = useState<string>('');
  const today = new Date();
  const currentYear = today.getFullYear();

  const leftPad = (val: number) => val.toString().padStart(2, '0');

  const days = useMemo(() => {
    if (selectedYear && selectedMonth) {
      const dayList = range(getDaysInMonth(new Date(Number(selectedYear), Number(selectedMonth) - 1)))
        .filter((day) => {
          let status = true;

          // to check available days if selected minDate/maxDate year & month
          if (
            minDate &&
            minDate.getFullYear() === Number(selectedYear) &&
            minDate.getMonth() === Number(selectedMonth) - 1
          ) {
            status = minDate.getDate() <= day + 1;
          }
          if (
            status &&
            maxDate &&
            maxDate.getFullYear() === Number(selectedYear) &&
            maxDate.getMonth() === Number(selectedMonth) - 1
          ) {
            status = maxDate.getDate() > day;
          }

          return status;
        })
        .map<SelectOption<string, string>>((day) => {
          const val = leftPad(day + 1);

          return { label: val, value: val };
        });

      const isInSelection = dayList.find((day) => day.value === selectedDay);
      if (!isInSelection) {
        setSelectedDay('');
      }

      return dayList;
    } else {
      setSelectedDay('');
    }

    return [];
  }, [selectedYear, selectedMonth]);

  const months = useMemo(() => {
    if (selectedYear) {
      const monthList = range(12)
        .filter((month) => {
          let status = true;

          // to check available months if selected minDate/maxDate year
          if (minDate && minDate.getFullYear() === Number(selectedYear)) {
            status = month >= minDate.getMonth();
          }
          if (status && maxDate && maxDate.getFullYear() === Number(selectedYear)) {
            status = month <= maxDate.getMonth();
          }

          return status;
        })
        .map<SelectOption<string, string>>((month) => {
          const val = leftPad(month + 1);

          return { label: val, value: val };
        });

      const isInSelection = monthList.find((month) => month.value === selectedMonth);
      if (!isInSelection) {
        setSelectedDay('');
        setSelectedMonth('');
      }

      return monthList;
    } else {
      setSelectedDay('');
      setSelectedMonth('');
    }

    return [];
  }, [selectedYear]);

  const years = range(currentYear - 70, currentYear + 70)
    .filter((year) => {
      let status = true;

      // to check available years between minDate & maxDate
      if (minDate) {
        status = minDate.getFullYear() <= year;
      }
      if (status && maxDate) {
        status = maxDate.getFullYear() >= year;
      }

      return status;
    })
    .map<SelectOption<string, string>>((year) => {
      const val = year.toString();

      return { label: val, value: val };
    });

  useEffect(() => {
    const isValidateDate = value && new Date(value);
    if (isValidateDate) {
      setSelectedDay(leftPad(new Date(value!).getDate()));
      setSelectedMonth(leftPad(new Date(value!).getMonth() + 1));
      setSelectedYear(new Date(value!).getFullYear().toString());
    }
  }, []);

  useEffect(() => {
    let date;

    if (selectedYear && selectedMonth && selectedDay) {
      const selectedDate = new Date([selectedYear, selectedMonth, selectedDay].join('-'));
      date = format ? formatDate(selectedDate, format) : selectedDate.toString();
    } else {
      date = '';
    }

    if (value !== date) {
      onChange?.(date);
    }
  }, [value, selectedYear, selectedMonth, selectedDay]);

  return (
    <div className={cn('flex gap-(--form-gap-x)', className)}>
      <Select
        options={years}
        invalid={hasError}
        disabled={disabled}
        className="flex-1"
        value={selectedYear}
        placeholder={t<string>('Selector.Year')}
        onChange={(val) => setSelectedYear(val)}
      />

      <Select
        options={months}
        invalid={hasError}
        disabled={disabled}
        className="flex-1"
        value={selectedMonth}
        placeholder={t<string>('Selector.Month')}
        onChange={(val) => setSelectedMonth(val)}
      />

      <Select
        options={days}
        invalid={hasError}
        disabled={disabled}
        className="flex-1"
        value={selectedDay}
        placeholder={t<string>('Selector.Day')}
        onChange={(val) => setSelectedDay(val)}
      />
    </div>
  );
};
